import React, { useState, useEffect } from 'react';
import IMEIEntryDialog from '../../inventory/components/IMEIEntryDialog';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  InputAdornment,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  Alert,
  Stack,
  Chip,
} from '@mui/material';
import { Close as CloseIcon, Euro as EuroIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { addVariant, editVariant } from '../../../app/store/slices/productVariantSlice';
import { toast } from 'react-toastify';

const EMPTY_FORM = {
  variantName: '',
  sku: '',
  barCode: '',
  salePrice: '',
  minSalePrice: '',
  costPrice: '',
  imageUrl: '',
  stockQuantity: '',
  minStockLevel: '',
  isSerializedProduct: false,
  attributes: '',
};

/**
 * VariantFormDialog – Create or edit a product variant.
 *
 * Props:
 *   open {bool}
 *   onClose {fn}
 *   productId {number}
 *   variant {Object|null}  – null = create mode, object = edit mode
 *   onSaved {fn(variant)}  – called after a successful save
 */
const VariantFormDialog = ({ open, onClose, productId, variant = null, onSaved }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { submitting, error } = useSelector((s) => s.productVariants);
  const isEdit = Boolean(variant?.id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState({});
  const [imeiDialogOpen, setImeiDialogOpen] = useState(false);
  const [imeiItems, setImeiItems] = useState([]);

  // Populate form when editing
  useEffect(() => {
    if (variant) {
      setForm({
        variantName: variant.variantName || '',
        sku: variant.sku || '',
        barCode: variant.barCode || '',
        salePrice: variant.salePrice ?? '',
        minSalePrice: variant.minSalePrice ?? '',
        costPrice: variant.costPrice ?? '',
        imageUrl: variant.imageUrl || '',
        stockQuantity: variant.stockQuantity ?? '',
        minStockLevel: variant.minStockLevel ?? '',
        isSerializedProduct: variant.isSerializedProduct || false,
        attributes: variant.attributes || '',
      });
      setImeiItems(variant.imeiItems || []);
    } else {
      setForm(EMPTY_FORM);
      setImeiItems([]);
    }
    setTouched({});
  }, [variant, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const validate = () => {
    const errors = {};
    if (!form.variantName.trim()) errors.variantName = t('required') || 'Required';
    if (!form.sku.trim()) errors.sku = t('required') || 'Required';
    if (form.salePrice === '' || isNaN(Number(form.salePrice))) errors.salePrice = t('invalid_price') || 'Invalid price';
    if (form.costPrice === '' || isNaN(Number(form.costPrice))) errors.costPrice = t('invalid_price') || 'Invalid price';
    return errors;
  };

  const errors = validate();
  const hasErrors = Object.keys(errors).length > 0;

  const handleSubmit = async () => {
    setTouched(Object.keys(form).reduce((acc, k) => ({ ...acc, [k]: true }), {}));
    if (hasErrors) return;

    const payload = {
      productId,
      variantName: form.variantName.trim(),
      sku: form.sku.trim(),
      barCode: form.barCode.trim() || null,
      salePrice: parseFloat(form.salePrice),
      minSalePrice: form.minSalePrice !== '' ? parseFloat(form.minSalePrice) : 0,
      costPrice: parseFloat(form.costPrice),
      imageUrl: form.imageUrl.trim() || null,
      stockQuantity: form.stockQuantity !== '' ? parseInt(form.stockQuantity, 10) : 0,
      minStockLevel: form.minStockLevel !== '' ? parseInt(form.minStockLevel, 10) : 0,
      isSerializedProduct: form.isSerializedProduct,
      attributes: form.attributes.trim() || null,
      imeiItems: form.isSerializedProduct ? imeiItems : [],
    };

    try {
      let result;
      if (isEdit) {
        result = await dispatch(editVariant({ id: variant.id, variantData: payload })).unwrap();
      } else {
        result = await dispatch(addVariant(payload)).unwrap();
      }
      toast.success(isEdit ? 'Variant updated' : 'Variant created');
      if (onSaved) onSaved(result);
      onClose();
    } catch {
      // error shown via redux state
    }
  };

  const fieldError = (name) => touched[name] && errors[name];

  // Prevent closing on backdrop click or Escape
  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          {isEdit ? 'Edit Variant' : 'Add Product Variant'}
        </Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          {/* Variant Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Variant Name"
              name="variantName"
              placeholder="e.g. Black / 256GB"
              value={form.variantName}
              onChange={handleChange}
              error={Boolean(fieldError('variantName'))}
              helperText={fieldError('variantName')}
            />
          </Grid>

          {/* SKU */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="SKU"
              name="sku"
              placeholder="e.g. IPH17-BLK-256"
              value={form.sku}
              onChange={handleChange}
              error={Boolean(fieldError('sku'))}
              helperText={fieldError('sku')}
            />
          </Grid>

          {/* Barcode */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Barcode"
              name="barCode"
              value={form.barCode}
              onChange={handleChange}
            />
          </Grid>

          {/* Sale Price */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Sale Price"
              name="salePrice"
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><EuroIcon fontSize="small" /></InputAdornment> }}
              value={form.salePrice}
              onChange={handleChange}
              error={Boolean(fieldError('salePrice'))}
              helperText={fieldError('salePrice')}
            />
          </Grid>

          {/* Min Sale Price */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Min Sale Price"
              name="minSalePrice"
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><EuroIcon fontSize="small" /></InputAdornment> }}
              value={form.minSalePrice}
              onChange={handleChange}
            />
          </Grid>

          {/* Cost Price */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Cost Price"
              name="costPrice"
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><EuroIcon fontSize="small" /></InputAdornment> }}
              value={form.costPrice}
              onChange={handleChange}
              error={Boolean(fieldError('costPrice'))}
              helperText={fieldError('costPrice')}
            />
          </Grid>

          {/* Stock */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Stock Quantity"
              name="stockQuantity"
              type="number"
              inputProps={{ min: 0 }}
              value={form.stockQuantity}
              onChange={handleChange}
              helperText="Leave 0 for serialized products (auto-calculated)"
            />
          </Grid>

          {/* Min Stock */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Min Stock Level"
              name="minStockLevel"
              type="number"
              inputProps={{ min: 0 }}
              value={form.minStockLevel}
              onChange={handleChange}
            />
          </Grid>

          {/* Attributes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Attributes (JSON, e.g. {"Color":"Black","Storage":"256GB"})'
              name="attributes"
              value={form.attributes}
              onChange={handleChange}
              placeholder='{"Color":"Black","Storage":"256GB"}'
              multiline
              rows={2}
            />
          </Grid>

          {/* Image URL */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Image URL"
              name="imageUrl"
              value={form.imageUrl}
              onChange={handleChange}
            />
          </Grid>

          {/* Serialized Toggle + IMEI Entry */}
          <Grid item xs={12}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="isSerializedProduct"
                    checked={form.isSerializedProduct}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Serialized Product (IMEI / Serial Tracking)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Enable to track individual items by IMEI or serial number
                    </Typography>
                  </Box>
                }
              />
              {form.isSerializedProduct && (
                <Box sx={{ mt: 2, border: '1px solid', borderColor: imeiItems.length > 0 ? 'success.main' : 'info.light', borderRadius: 2, p: 1.5, bgcolor: imeiItems.length > 0 ? 'success.50' : 'info.50' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Inventory Items (IMEI / Serial Numbers)</Typography>
                      <Typography variant="caption" color="text.secondary">Add IMEI numbers for initial stock.</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {imeiItems.length > 0 && <Chip label={`${imeiItems.length} entered`} color="success" size="small" />}
                      <Button size="small" variant="outlined" color="info" onClick={() => setImeiDialogOpen(true)} sx={{ borderRadius: 1.5, textTransform: 'none' } }>
                        {imeiItems.length > 0 ? 'Edit IMEIs' : 'Add IMEIs'}
                      </Button>
                    </Stack>
                  </Stack>
                  {imeiItems.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {imeiItems.map((item, i) => (
                        <Chip key={i} label={item.imei} size="small" color="info" variant="outlined" />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
              {/* IMEI Entry Dialog */}
              <IMEIEntryDialog
                open={imeiDialogOpen}
                onClose={() => setImeiDialogOpen(false)}
                quantity={Math.max(1, parseInt(form.stockQuantity) || 1)}
                variantName={form.variantName || 'Variant'}
                existingItems={imeiItems}
                onSave={(items) => {
                  setImeiItems(items);
                  setImeiDialogOpen(false);
                }}
              />
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {isEdit ? 'Update Variant' : 'Add Variant'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VariantFormDialog;
